<template>
  <div class="m-2">
    <div class="mainheader d-flex justify-content-left p-2">
      <span>Patmatch</span>
      <img
        src="@/assets/diamond.svg"
        height="20px"
        alt="Premium"
        class="ml-2"
      />
    </div>

    <div class="pl-2 text-left">
      <p>
        Pattern Matching allows you to search for short (&lt;20 residues)
        nucleotide or peptide sequences, or ambiguous/degenerate patterns. It
        uses the same Arabidopsis dataset as TAIR's
        <a href="https://www.arabidopsis.org/tools/blast/">BLAST</a>. If you are
        searching for a sequence &gt;20 bp or aa with no degenerate positions,
        please use BLAST, which is much faster. Pattern Matching allows for
        ambiguous characters, mismatches, insertions and deletions, but does not
        do alignments and so is not a replacement for BLAST. Currently the
        maximum number of hits retrieved is 250,000 and the minimum number of
        input string is 3 residues.
      </p>
      <p>
        <a
          href="https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42217191/PatMatch+Notes"
          target="_blank"
          >Version 1.1 Release Notes</a
        >
      </p>
    </div>

    <div class="container p-2 border rounded shadow-sm form-bg">
      <form @submit.prevent="handleSubmit" @reset.prevent="handleReset">
        <!-- Sequence Input -->
        <fieldset class="border border-dark rounded p-2">
          <span class="pl-4 mb-3 d-flex justify-content-left">
            <b>Enter a sequence or pattern</b> &nbsp;(<a href="#syntax"
              >examples</a
            >)
          </span>
          <div class="form-group row">
            <label for="seq_class" class="col-sm-4 col-form-label text-left"
              >Sequence Type:</label
            >
            <div class="col-sm-4">
              <select
                id="seq_class"
                v-model="form.seq_class"
                class="form-control"
                @change="onSequenceTypeChange"
              >
                <option value="nucleotide">Nucleotide</option>
                <option value="peptide">Peptide</option>
              </select>
            </div>
          </div>
          <div class="form-group row">
            <label for="pattern" class="col-sm-4 col-form-label text-left"
              >Sequence or Pattern:</label
            >
            <div class="col-sm-8">
              <input
                id="pattern"
                v-model="form.pat"
                class="form-control"
                placeholder="Enter sequence or pattern"
                required
              />
            </div>
          </div>
        </fieldset>

        <!-- Database Selection -->
        <fieldset class="border border-dark rounded p-2 mt-4">
          <span class="pl-4 mb-3 d-flex justify-content-left">
            <b>Choose a Sequence Database:</b>
          </span>
          <p class="pl-4 text-left">
            All public Arabidopsis sequences can be found within these datasets.
            (<a
              href="https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42217028/TAIR+BLAST+Help#TAIRBLASTHelp-TAIRDatasets"
              target="_blank"
              >Datasets Description</a
            >)
          </p>
          <div class="form-group row">
            <label for="current_db" class="col-sm-4 col-form-label text-left"
              >Database:</label
            >
            <div class="col-sm-8">
              <select
                id="current_db"
                v-model="form.current_db"
                class="form-control"
                :disabled="!datasetGroups.length"
              >
                <optgroup
                  v-for="group in datasetGroups"
                  :key="group.name"
                  :label="group.name"
                >
                  <option
                    v-for="option in group.options"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{ option.label }}
                  </option>
                </optgroup>
              </select>
            </div>
          </div>
        </fieldset>

        <!-- Advanced Options -->
        <fieldset class="border border-dark rounded p-2 mt-4">
          <span class="pl-4 mb-3 d-flex justify-content-left">
            <b>More Options:</b>
          </span>
          <div class="form-group row">
            <label for="max_hits" class="col-sm-4 col-form-label text-left"
              >Maximum Hits:</label
            >
            <div class="col-sm-4">
              <select
                id="max_hits"
                v-model="form.max_hits"
                class="form-control"
              >
                <option
                  v-for="hits in maxHitsOptions"
                  :key="hits"
                  :value="hits"
                >
                  {{ hits }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-group row">
            <label for="strand" class="col-sm-4 col-form-label text-left"
              >DNA Strand:</label
            >
            <div class="col-sm-4">
              <select id="strand" v-model="form.strand" class="form-control">
                <option value="both">Both strands</option>
                <option value="Watson">Watson (given)</option>
                <option value="Crick">Crick (reverse complement)</option>
              </select>
            </div>
          </div>
          <div class="form-group row">
            <label for="mismatch" class="col-sm-4 col-form-label text-left"
              >Mismatch:</label
            >
            <div class="col-sm-4">
              <select
                id="mismatch"
                v-model="form.mismatch"
                class="form-control"
                @change="updateMismatchCheckboxes"
              >
                <option
                  v-for="mismatch in mismatchOptions"
                  :key="mismatch"
                  :value="mismatch"
                >
                  {{ mismatch }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-group row">
            <label class="col-sm-4 col-form-label text-left"
              >Mismatch Type:</label
            >
            <div class="col-sm-8">
              <div class="form-check text-left">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="insertion"
                  v-model="form.insertion"
                  @change="updateMismatchDropdown"
                />
                <label for="insertion" class="form-check-label"
                  >Insertions</label
                >
              </div>
              <div class="form-check text-left">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="deletion"
                  v-model="form.deletion"
                  @change="updateMismatchDropdown"
                />
                <label for="deletion" class="form-check-label">Deletions</label>
              </div>
              <div class="form-check text-left">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="substitution"
                  v-model="form.substitution"
                  @change="updateMismatchDropdown"
                />
                <label for="substitution" class="form-check-label"
                  >Substitutions</label
                >
              </div>
            </div>
          </div>
          <div class="form-group row">
            <label for="min_seq_hits" class="col-sm-4 col-form-label text-left"
              >Minimum Hits per Sequence:</label
            >
            <div class="col-sm-4">
              <input
                type="number"
                id="min_seq_hits"
                v-model="form.min_seq_hits"
                class="form-control"
                min="1"
                max="99999"
              />
            </div>
          </div>
          <div class="form-group row">
            <label for="max_seq_hits" class="col-sm-4 col-form-label text-left"
              >Maximum Hits per Sequence:</label
            >
            <div class="col-sm-4">
              <input
                type="number"
                id="max_seq_hits"
                v-model="form.max_seq_hits"
                class="form-control"
                min="1"
                max="99999"
              />
            </div>
          </div>
        </fieldset>

        <div
          v-if="errorMessage"
          class="alert alert-danger text-left"
          role="alert"
        >
          {{ errorMessage }}
        </div>

        <!-- Submit and Reset Buttons -->
        <div class="form-group row">
          <div class="col-sm-4 offset-sm-1 d-flex justify-content-left mt-2">
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="submitting || !form.current_db"
              :aria-busy="submitting ? 'true' : 'false'"
            >
              <span
                v-if="submitting"
                class="spinner-border spinner-border-sm mr-2"
                aria-hidden="true"
              ></span>
              Start Pattern Search
            </button>
            <button type="reset" class="ml-4 btn btn-secondary">Reset</button>
          </div>
        </div>
      </form>

      <!-- Pattern Syntax Reference -->
      <div class="mt-4">
        <a name="syntax"></a>
        <h3 class="mb-3">Supported Pattern Syntax and Examples:</h3>
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Search Type</th>
                <th>Character</th>
                <th>Meaning</th>
                <th>Examples</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th rowspan="6">Peptide Searches</th>
                <td>IFVLWMAGCYP TSHEDQNKR</td>
                <td>Exact match</td>
                <td>DQGT</td>
              </tr>
              <tr>
                <td>J</td>
                <td>Any hydrophobic residue (IFVLWMAGCY)</td>
                <td>AAAAAAJJ</td>
              </tr>
              <tr>
                <td>O</td>
                <td>Any hydrophilic residue (TSHEDQNKR)</td>
                <td>TTTTTTOO</td>
              </tr>
              <tr>
                <td>B</td>
                <td>D or N</td>
                <td>FLGB</td>
              </tr>
              <tr>
                <td>Z</td>
                <td>E or Q</td>
                <td>GLFGZ</td>
              </tr>
              <tr>
                <td>X or .</td>
                <td>Any amino acid</td>
                <td>DXXXNW..VSK</td>
              </tr>
              <tr>
                <th rowspan="12">Nucleotide searches</th>
                <td>ACTGU</td>
                <td>Exact match</td>
                <td>ACCGGCGTAA</td>
              </tr>
              <tr>
                <td>R</td>
                <td>Any purine base (AG)</td>
                <td>AAGGCCGGRRRR</td>
              </tr>
              <tr>
                <td>Y</td>
                <td>Any pyrimidine base (CT)</td>
                <td>CCCATAYYGGYY</td>
              </tr>
              <tr>
                <td>S</td>
                <td>G or C</td>
                <td rowspan="4">YGGTWCAMWTGTY</td>
              </tr>
              <tr>
                <td>W</td>
                <td>A or T</td>
              </tr>
              <tr>
                <td>M</td>
                <td>A or C</td>
              </tr>
              <tr>
                <td>K</td>
                <td>G or T</td>
              </tr>
              <tr>
                <td>V</td>
                <td>A or C or G</td>
                <td rowspan="4">CCGG...WHW.{3,5}HWH...CCGG</td>
              </tr>
              <tr>
                <td>H</td>
                <td>A or C or T</td>
              </tr>
              <tr>
                <td>D</td>
                <td>A or G or T</td>
              </tr>
              <tr>
                <td>B</td>
                <td>C or G or T</td>
              </tr>
              <tr>
                <td>N or X or .</td>
                <td>Any base</td>
                <td>ATGCTNNNNATCG</td>
              </tr>
              <tr>
                <th rowspan="6">All searches:</th>
                <td>[ ]</td>
                <td>A subset of elements<br />[TC] = T or C</td>
                <td>[WFY]XXXDN[RK][ST]</td>
              </tr>
              <tr>
                <td>[^ ]</td>
                <td>An excluded subset of elements<br />[^TA] = not T or A</td>
                <td>NDBB...[VILM]Z[DE]...[^PG]</td>
              </tr>
              <tr>
                <td>( )</td>
                <td>Specifies a sub-pattern</td>
                <td>(YDXXX){2,}</td>
              </tr>
              <tr>
                <td>{m,n}</td>
                <td>
                  {m} = exactly m times<br />{m,} = at least m times<br />{,m} =
                  0 to m times
                </td>
                <td>L{3,5}X{5}DGZ</td>
              </tr>
              <tr>
                <td>&lt;</td>
                <td>Constrains pattern to N-terminus or 5' end</td>
                <td>&lt;MNTD (pep)<br />&lt;ATGX{6,10}RTTRTT (nuc)</td>
              </tr>
              <tr>
                <td>&gt;</td>
                <td>Constrains pattern to C-terminus or 3' end</td>
                <td>SBGZ&gt; (pep)<br />YATTRTGA&gt; (nuc)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PostsService from "@/services/PostsService";

const defaultForm = () => ({
  seq_class: "nucleotide",
  pat: "",
  current_db: "",
  max_hits: "75000",
  strand: "both",
  mismatch: "0",
  insertion: false,
  deletion: false,
  substitution: false,
  min_seq_hits: "1",
  max_seq_hits: "100",
});

export default {
  data() {
    return {
      form: defaultForm(),
      datasets: [],
      submitting: false,
      errorMessage: "",
      maxHitsOptions: [
        25, 50, 100, 200, 500, 1000, 75000, 100000, 150000, 200000, 250000,
      ],
      mismatchOptions: ["0", "1", "2", "3"],
    };
  },
  computed: {
    datasetType() {
      return this.form.seq_class === "peptide" ? "AA" : "DNA";
    },
    // Datasets for the sequence type, as <optgroup>s in server order.
    datasetGroups() {
      const groups = [];
      for (const d of this.datasets) {
        if (d.type !== this.datasetType) continue;
        let group = groups[groups.length - 1];
        if (!group || group.name !== d.group) {
          group = { name: d.group, options: [] };
          groups.push(group);
        }
        group.options.push(d);
      }
      return groups;
    },
  },
  async mounted() {
    try {
      const { data } = await PostsService.getPatmatchDatasets();
      this.datasets = data.datasets || [];
      this.onSequenceTypeChange();
    } catch (err) {
      this.errorMessage =
        "Could not load the PatMatch datasets. Please reload the page.";
    }
  },
  methods: {
    updateMismatchCheckboxes() {
      if (
        this.form.mismatch !== "0" &&
        !this.form.insertion &&
        !this.form.deletion &&
        !this.form.substitution
      ) {
        this.form.insertion = true;
        this.form.deletion = true;
        this.form.substitution = true;
      }
      if (this.form.mismatch === "0") {
        this.form.insertion = false;
        this.form.deletion = false;
        this.form.substitution = false;
      }
    },
    updateMismatchDropdown() {
      if (
        this.form.mismatch === "0" &&
        (this.form.insertion || this.form.deletion || this.form.substitution)
      ) {
        this.form.mismatch = "1";
      }
      if (
        this.form.mismatch !== "0" &&
        !this.form.insertion &&
        !this.form.deletion &&
        !this.form.substitution
      ) {
        this.form.mismatch = "0";
      }
    },
    async handleSubmit() {
      this.errorMessage = "";
      this.submitting = true;
      try {
        const { data } = await PostsService.submitPatmatch({
          seqClass: this.form.seq_class,
          pattern: this.form.pat,
          dataset: this.form.current_db,
          strand: this.form.strand,
          maxHits: this.form.max_hits,
          mismatch: this.form.mismatch,
          insertion: this.form.insertion,
          deletion: this.form.deletion,
          substitution: this.form.substitution,
          minSeqHits: this.form.min_seq_hits,
          maxSeqHits: this.form.max_seq_hits,
        });
        this.$router.push({
          name: "PatmatchResults",
          params: { jobId: data.jobId },
        });
      } catch (err) {
        this.errorMessage =
          (err.response && err.response.data && err.response.data.error) ||
          "PatMatch could not be started. Please try again later.";
      } finally {
        this.submitting = false;
      }
    },
    handleReset() {
      this.form = defaultForm();
      this.errorMessage = "";
      this.onSequenceTypeChange();
    },
    // Keep the chosen database if it fits the sequence type, else the first.
    onSequenceTypeChange() {
      const ok = this.datasets.some(
        (d) => d.id === this.form.current_db && d.type === this.datasetType
      );
      if (!ok) {
        const first = this.datasets.find((d) => d.type === this.datasetType);
        this.form.current_db = first ? first.id : "";
      }
    },
  },
};
</script>

<style scoped>
.mainheader {
  font-size: 16pt;
  font-weight: bold;
  color: #930;
}
.container {
  max-width: 100%;
}
.form-bg {
  background: #f1efec;
}
fieldset {
  margin-bottom: 1rem;
}
.table th {
  background-color: #f8f9fa;
}
</style>
